import { useCallback,useMemo, useState } from "react";
import moment from "moment";

const normalizePriority = (priority) => {
  if (priority === null || priority === undefined) return null;

  if (typeof priority === "number") return priority;

  if (typeof priority === "string") {
    const numericValue = parseInt(priority, 10);
    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 2) {
      return numericValue;
    }

    const priorityMap = {
      "LOW": 0,
      "NORMAL": 1,
      "HIGH": 2,
    };
    return priorityMap[priority.toUpperCase()] ?? null;
  }

  return null;
};

export const useDocumentFilters = (documents = [], options = {}) => {
  const { searchByPatient = false } = options;
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [priorityFilter, setPriorityFilterState] = useState([]);
  const [sortField, setSortField] = useState("created");
  const [sortDirection, setSortDirection] = useState("desc");

  const filteredDocuments = useMemo(() => {
    if (!documents || documents.length === 0) return [];

    return documents.filter((doc) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const fileNameMatch = doc.fileName?.toLowerCase().includes(query);
        const docNameMatch = doc.name?.toLowerCase().includes(query);

        let patientMatch = false;
        if (searchByPatient) {
          const patientFirstName = doc.patient?.firstName?.toLowerCase() || "";
          const patientSurname = doc.patient?.surname?.toLowerCase() || "";
          const patientFullName = `${patientFirstName} ${patientSurname}`.trim();
          patientMatch = patientFirstName.includes(query) ||
                         patientSurname.includes(query) ||
                         patientFullName.includes(query);
        }

        if (!fileNameMatch && !docNameMatch && !patientMatch) return false;
      }

      if (statusFilter.length > 0) {
        const normalizeStatus = (status) =>
          status?.toUpperCase().replace(/[\s_-]+/g, "_") || "";
        const docStatus = normalizeStatus(doc.status);
        const hasMatchingStatus = statusFilter.some(
          (filterStatus) => normalizeStatus(filterStatus) === docStatus
        );
        if (!hasMatchingStatus) {
          return false;
        }
      }

      if (priorityFilter.length > 0) {
        const docPriority = normalizePriority(doc.priority);
        const hasNullFilter = priorityFilter.some((p) => p === null);
        const hasMatchingPriority =
          priorityFilter.includes(docPriority) ||
          (hasNullFilter && docPriority === null);
        if (!hasMatchingPriority) {
          return false;
        }
      }

      return true;
    });
  }, [documents, searchQuery, statusFilter, priorityFilter, searchByPatient]);

  const sortedDocuments = useMemo(() => {
    if (!filteredDocuments || filteredDocuments.length === 0) return [];

    return [...filteredDocuments].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "name":
          aValue = a.name?.toLowerCase() || "";
          bValue = b.name?.toLowerCase() || "";
          break;
        case "status":
          const statusOrder = {
            "NEW": 0,
            "IN_REVIEW": 2,
            "UPLOADED": 3,
            "REVIEWED": 4,
            "SYSTEM_GENERATED": 5,
          };
          const aStatus = a.status?.toUpperCase() || "";
          const bStatus = b.status?.toUpperCase() || "";
          aValue = statusOrder[aStatus] ?? 99;
          bValue = statusOrder[bStatus] ?? 99;
          break;
        case "priority":
          const aPriority = normalizePriority(a.priority);
          const bPriority = normalizePriority(b.priority);

          if (aPriority === null) {
            if (bPriority === null) return 0;
            return 1;
          }
          if (bPriority === null) {
            return -1;
          }

          aValue = aPriority;
          bValue = bPriority;
          break;
        case "created":
        default:
          const getDateValue = (doc) => {
            const dateField =
              doc.created ||
              doc.uploadedDate ||
              doc.dateUploaded ||
              doc.createdDate ||
              doc.date;

            if (!dateField) {
              return 0;
            }

            if (Array.isArray(dateField) && dateField.length >= 3) {
              const momentDate = moment(
                new Date(
                  dateField[0],
                  dateField[1] - 1,
                  dateField[2],
                  dateField[3] || 0,
                  dateField[4] || 0,
                  dateField[5] || 0
                )
              );
              return momentDate.isValid() ? momentDate.valueOf() : 0;
            }

            if (typeof dateField === "string") {
              const formats = [
                "YYYY-MM-DD",
                "DD-MM-YYYY",
                "DD/MM/YYYY",
                "YYYY-MM-DD HH:mm",
                "YYYY-MM-DD HH:mm:ss",
                "DD-MM-YYYY HH:mm",
                "DD-MM-YYYY HH:mm:ss",
                "DD/MM/YYYY HH:mm",
                "DD/MM/YYYY HH:mm:ss",
                "YYYY-MM-DDTHH:mm:ss",
                "YYYY-MM-DDTHH:mm:ss.SSS",
                "YYYY-MM-DDTHH:mm:ss.SSSZ",
                moment.ISO_8601,
              ];
              const momentDate = moment(dateField, formats, true);
              if (momentDate.isValid()) {
                return momentDate.valueOf();
              }
              const fallbackDate = moment(dateField);
              if (fallbackDate.isValid()) {
                return fallbackDate.valueOf();
              }
            }

            const momentDate = moment(dateField);
            return momentDate.isValid() ? momentDate.valueOf() : 0;
          };

          aValue = getDateValue(a);
          bValue = getDateValue(b);
          break;
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [filteredDocuments, sortField, sortDirection]);

  const setSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const setStatus = useCallback((status) => {
    setStatusFilter(Array.isArray(status) ? status : status ? [status] : []);
  }, []);

  const setPriority = useCallback((priority) => {
    setPriorityFilterState(
      Array.isArray(priority) ? priority : priority !== null ? [priority] : []
    );
  }, []);

  const setSort = useCallback((field, direction = "desc") => {
    setSortField(field);
    setSortDirection(direction);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setStatusFilter([]);
    setPriorityFilterState([]);
    setSortField("created");
    setSortDirection("desc");
  }, []);

  return {
    documents: sortedDocuments,
    totalDocuments: documents?.length || 0,
    searchQuery,
    statusFilter,
    priorityFilter,
    sortField,
    sortDirection,
    setSearch,
    setStatus,
    setPriority,
    setSort,
    clearFilters,
  };
};
